import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useProgramContext } from '../contexts/ProgramContext';
import { isUserAdmin } from '../utils/adminCheck';
import { UserDashboard } from './UserDashboard';
import { EpochHistory } from './EpochHistory';
import UserStaking from './UserStaking';
import AdminPanel from './AdminPanel';
import GlobalConfig from './GlobalConfig';
import EpochManagement from './EpochManagement';

type TabType = 'dashboard' | 'staking' | 'history' | 'epochs' | 'config' | 'admin';

const StakingInterface: React.FC = () => {
  const { connected, publicKey } = useWallet();
  const { program } = useProgramContext();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Check if connected wallet is an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!program || !connected || !publicKey) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }

      setCheckingAdmin(true);
      const adminStatus = await isUserAdmin(program, publicKey);
      setIsAdmin(adminStatus);
      setCheckingAdmin(false);
    };

    checkAdminStatus();
  }, [program, connected, publicKey]);

  if (!connected) {
    return <UserDashboard />;
  }

  const userTabs = [
    { id: 'dashboard' as TabType, label: 'Dashboard' },
    { id: 'staking' as TabType, label: 'Stake/Unstake' },
    { id: 'history' as TabType, label: 'Epoch History' },
  ];

  const adminTabs = [
    { id: 'epochs' as TabType, label: 'Epoch Management' },
    { id: 'config' as TabType, label: 'Global Config' },
    { id: 'admin' as TabType, label: 'Admin Panel' },
  ];

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="bg-white rounded-habitat-lg shadow-habitat border border-habitat-neutral-200">
        <div className="border-b border-habitat-neutral-200">
          <nav className="flex space-x-4 px-6" aria-label="Tabs">
            {userTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-4 px-3 text-sm font-medium border-b-2 transition-colors duration-200
                  ${
                    activeTab === tab.id
                      ? 'border-habitat-primary text-habitat-primary'
                      : 'border-transparent text-habitat-neutral-500 hover:text-habitat-neutral-700 hover:border-habitat-neutral-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Admin Tabs */}
        {isAdmin && !checkingAdmin && (
          <div className="border-b border-habitat-neutral-200 bg-habitat-neutral-50">
            <div className="px-6 py-2">
              <p className="text-xs font-semibold text-habitat-primary uppercase tracking-wide mb-2">
                Admin Section
              </p>
              <nav className="flex space-x-4 pb-2" aria-label="Admin Tabs">
                {adminTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      py-2 px-3 text-sm font-medium rounded-habitat transition-colors duration-200
                      ${
                        activeTab === tab.id
                          ? 'bg-habitat-primary text-white'
                          : 'text-habitat-neutral-600 hover:bg-habitat-neutral-200'
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className="transition-all duration-300">
        {activeTab === 'dashboard' && <UserDashboard />}
        {activeTab === 'staking' && <UserStaking />}
        {activeTab === 'history' && <EpochHistory />}
        {activeTab === 'epochs' && isAdmin && <EpochManagement />}
        {activeTab === 'config' && isAdmin && <GlobalConfig />}
        {activeTab === 'admin' && isAdmin && <AdminPanel />}
      </div>
    </div>
  );
};

export default StakingInterface;
